import { Bell } from 'lucide-react';
import { Meter } from './Meter';
import type { BookabilityStats } from '../types';

const MONTH_NAMES: Record<string, string> = {
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec',
};

function bookabilitySentence(stats: BookabilityStats): string {
  if (stats.bookedPct >= 0.97) {
    return `Nearly sold out — ${stats.openSiteNights.toLocaleString()} of ${stats.totalSiteNights.toLocaleString()} site-nights left. Openings go fast.`;
  }
  if (stats.weekend.bookedPct >= 0.97) {
    return `Weekends are sold out — ${stats.weekday.openSiteNights.toLocaleString()} weekday site-nights still open.`;
  }
  if (stats.bookedPct >= 0.8) {
    return `Filling up — ${stats.openSiteNights.toLocaleString()} site-nights still open.`;
  }
  return `Good availability — ${stats.openSiteNights.toLocaleString()} of ${stats.totalSiteNights.toLocaleString()} site-nights open.`;
}

interface AvailabilitySnapshotProps {
  stats: BookabilityStats;
  onAlertClick?: () => void;
}

export function AvailabilitySnapshot({ stats, onAlertClick }: AvailabilitySnapshotProps) {
  const pct = Math.round(stats.bookedPct * 100);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700">
        Bookability · next {stats.windowDays} days
      </p>
      <p className="mt-3 flex items-baseline gap-2">
        <span className="text-4xl font-bold tabular-nums text-gray-900">{pct}%</span>
        <span className="text-gray-500">booked</span>
      </p>
      <div className="mt-3">
        <Meter pct={stats.bookedPct} size="lg" tone="light" />
      </div>
      <p className="mt-2 text-sm text-gray-600">{bookabilitySentence(stats)}</p>
      <div className="mt-4 space-y-3">
        <div className="flex items-center gap-3">
          <p className="text-xs text-gray-500 w-32 flex-shrink-0">Weekends (Fri–Sat)</p>
          <Meter pct={stats.weekend.bookedPct} size="sm" tone="light" />
          <p className="text-xs tabular-nums text-gray-700 w-9 text-right flex-shrink-0">
            {Math.round(stats.weekend.bookedPct * 100)}%
          </p>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-xs text-gray-500 w-32 flex-shrink-0">Weekdays</p>
          <Meter pct={stats.weekday.bookedPct} size="sm" tone="light" />
          <p className="text-xs tabular-nums text-gray-700 w-9 text-right flex-shrink-0">
            {Math.round(stats.weekday.bookedPct * 100)}%
          </p>
        </div>
      </div>
      {stats.byMonth.length > 1 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {stats.byMonth.map((bucket) => (
            <div key={bucket.month} className="bg-gray-50 rounded-lg px-3 py-2 min-w-[64px]">
              <p className="text-[11px] uppercase tracking-wider text-gray-400">
                {MONTH_NAMES[bucket.month] ?? bucket.month}
              </p>
              <p className="text-sm font-semibold text-gray-800 tabular-nums">
                {Math.round(bucket.bookedPct * 100)}%
              </p>
              <div className="mt-1.5">
                <Meter pct={bucket.bookedPct} size="sm" tone="light" />
              </div>
            </div>
          ))}
        </div>
      )}
      {onAlertClick && (
        <button
          type="button"
          onClick={onAlertClick}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
        >
          <Bell className="w-4 h-4" />
          Get availability alerts
        </button>
      )}
      {stats.dataAgeHours != null && (
        <p className="mt-4 text-[11px] text-gray-400 tabular-nums">
          {stats.dataAgeHours === 0 ? 'Just checked' : `Checked ${stats.dataAgeHours}h ago`}
        </p>
      )}
    </div>
  );
}
